import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  HStack,
  IconButton,
  Progress,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import { FiCopy } from 'react-icons/fi'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { formatPaymentAmount } from '@/utils/amountUtils'
import { formatDateTime } from '@/utils/formatUtils'
import { useApiError } from '@/hooks/useApiError'
import { useToast } from '@/hooks/useToast'
import { systemService, type SystemPlatformWallet } from '@/services/admin/systemService'
import type {
  PlatformWalletBalanceMonitorWallet,
  PlatformWalletChainBalance,
  TronUsageDeficit,
} from '@/types'
import { useNetworkEnvironment, type NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { ChainBadge } from '@/components/common/ChainBadge'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import {
  resolveMostSevereThresholdStatus,
  resolveThresholdColorScheme,
} from '@/components/monitoring/platformWalletThresholdPresentation'

const SNAPSHOT_CONCURRENCY = 1
const SNAPSHOT_RETRY_DELAYS_MS = [0, 300, 900] as const
const ROLE_ORDER = [
  'deployment',
  'operator',
  'governor',
  'refunder',
  'expiry_payments_receiver',
  'rent_receiver',
] as const
const COMPATIBILITY_ORDER = ['evm', 'tron', 'solana'] as const
type SupportedRole = (typeof ROLE_ORDER)[number]
type SupportedCompatibility = (typeof COMPATIBILITY_ORDER)[number]

type RoleWalletEntry = {
  id: string
  role: SupportedRole
  compatibility: SupportedCompatibility
  address: string
}

type RoleWalletSnapshot = {
  wallet: RoleWalletEntry
  chains: PlatformWalletChainBalance[]
  updatedAt?: string
  runtimeAddress?: string
  configurationIssue?: 'runtime_signer_unavailable' | 'runtime_address_mismatch'
  error?: string
}

type LoadRoleSnapshotsResult = {
  snapshots: RoleWalletSnapshot[]
  tronError?: string
}

const EVM_CHAIN_ORDER: Record<NetworkEnvironment, string[]> = {
  mainnet: ['1', '56', '137', '8453'],
  testnet: ['11155111', '97', '80002', '84532'],
}

const TRON_CHAIN_ORDER: Record<NetworkEnvironment, string[]> = {
  mainnet: ['728126428'],
  testnet: ['3448148188'],
}

const SOLANA_CHAIN_ORDER: Record<NetworkEnvironment, string[]> = {
  mainnet: ['101'],
  testnet: ['103'],
}

const formatBalance = (balance?: string | null, decimals?: number): string => {
  if (!balance || decimals == null) {
    return '--'
  }
  const displayDecimals = Math.min(6, Math.max(decimals, 0))
  return formatPaymentAmount(balance, displayDecimals, true)
}

const formatApproxUsdt = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '--'
  }
  if (value < 0.01) {
    return formatPaymentAmount(value, 4, true)
  }
  if (value < 1) {
    return formatPaymentAmount(value, 3, true)
  }
  if (value < 100) {
    return formatPaymentAmount(value, 2, true)
  }
  return formatPaymentAmount(value, 2, true)
}

const formatResourceValue = (value?: number | null): string => {
  if (value == null || Number.isNaN(value)) {
    return '--'
  }
  return value.toLocaleString('en-US')
}

const buildResourceProgress = (remaining?: number | null, limit?: number | null) => {
  if (
    remaining == null ||
    limit == null ||
    Number.isNaN(remaining) ||
    Number.isNaN(limit) ||
    limit <= 0
  ) {
    return { label: '-- / --', value: 0 }
  }
  const ratio = Math.max(0, Math.min(1, remaining / limit))
  return {
    label: `${formatResourceValue(remaining)} / ${formatResourceValue(limit)}`,
    value: ratio * 100,
  }
}

const formatTrxValue = (value?: number | null): string => {
  if (value == null || Number.isNaN(value)) {
    return '--'
  }
  return formatPaymentAmount(value, 6, true)
}

const sortByOrder = (balances: PlatformWalletChainBalance[], order: string[]) => {
  const weight = new Map(order.map((value, index) => [value, index]))
  return [...balances].sort((a, b) => {
    const indexA = weight.get(a.chainId) ?? Number.MAX_SAFE_INTEGER
    const indexB = weight.get(b.chainId) ?? Number.MAX_SAFE_INTEGER
    return indexA - indexB
  })
}

const sortRoleWallets = (wallets: RoleWalletEntry[]): RoleWalletEntry[] => {
  const compatibilityWeight = new Map(
    COMPATIBILITY_ORDER.map((value, index) => [value, index])
  )
  const roleWeight = new Map(ROLE_ORDER.map((value, index) => [value, index]))
  return [...wallets].sort((a, b) => {
    const compatibilityA = compatibilityWeight.get(a.compatibility) ?? Number.MAX_SAFE_INTEGER
    const compatibilityB = compatibilityWeight.get(b.compatibility) ?? Number.MAX_SAFE_INTEGER
    if (compatibilityA !== compatibilityB) {
      return compatibilityA - compatibilityB
    }
    const roleA = roleWeight.get(a.role) ?? Number.MAX_SAFE_INTEGER
    const roleB = roleWeight.get(b.role) ?? Number.MAX_SAFE_INTEGER
    return roleA - roleB
  })
}

const sortRoleSnapshots = (
  snapshots: RoleWalletSnapshot[]
): RoleWalletSnapshot[] => {
  const order = new Map(
    sortRoleWallets(snapshots.map(snapshot => snapshot.wallet))
      .map((wallet, index) => [wallet.id, index])
  )
  return [...snapshots].sort(
    (a, b) => (order.get(a.wallet.id) ?? 0) - (order.get(b.wallet.id) ?? 0)
  )
}

const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  if (items.length === 0) {
    return []
  }

  const results = new Array<R>(items.length)
  let cursor = 0
  const workerCount = Math.max(1, Math.min(limit, items.length))
  const nextIndex = (): number | null => {
    if (cursor >= items.length) {
      return null
    }
    const index = cursor
    cursor += 1
    return index
  }

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      for (let index = nextIndex(); index !== null; index = nextIndex()) {
        results[index] = await mapper(items[index], index)
      }
    })
  )

  return results
}

const delay = async (ms: number): Promise<void> =>
  await new Promise(resolve => {
    setTimeout(resolve, ms)
  })

const resolveErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const message = error.message.trim()
    if (message.length > 0) {
      return message
    }
  }
  return String(error)
}

const normalizeRoleWallet = (
  wallet: SystemPlatformWallet
): RoleWalletEntry | null => {
  const compatibility = wallet.compatibility.trim().toLowerCase()
  const role = wallet.role.trim().toLowerCase()
  const address = wallet.address.trim()

  if (!wallet.isActive) {
    return null
  }
  if (compatibility !== 'evm' && compatibility !== 'tron' && compatibility !== 'solana') {
    return null
  }
  if (!ROLE_ORDER.includes(role as SupportedRole)) {
    return null
  }
  if (!address) {
    return null
  }

  return {
    id: wallet.id,
    compatibility,
    role: role as SupportedRole,
    address,
  }
}

const normalizeBalanceMonitorWallet = (
  wallet: PlatformWalletBalanceMonitorWallet,
  mode: NetworkEnvironment,
  updatedAt: string
): RoleWalletSnapshot | null => {
  const role = wallet.role.trim().toLowerCase()
  const address = wallet.address.trim()
  if (!ROLE_ORDER.includes(role as SupportedRole) || !address) {
    return null
  }

  const chains = wallet.chains.map(chain => ({
    chainId: chain.chainId,
    chainCode: chain.chainCode,
    chainName: chain.chainName,
    network: mode,
    compatibility: wallet.compatibility,
    symbol: chain.symbol,
    decimals: chain.decimals,
    balance: chain.balance,
    nativeTokenPrice: chain.nativeTokenPrice,
    updatedAt,
    error: chain.error,
    thresholds: chain.thresholds,
  }))
  const chainOrder = wallet.compatibility === 'evm'
    ? EVM_CHAIN_ORDER[mode]
    : SOLANA_CHAIN_ORDER[mode]

  return {
    wallet: {
      id: wallet.id,
      role: role as SupportedRole,
      compatibility: wallet.compatibility,
      address,
    },
    chains: sortByOrder(chains, chainOrder),
    updatedAt,
    runtimeAddress: wallet.runtimeAddress,
    configurationIssue: wallet.configurationIssue,
    error: wallet.error,
  }
}

export const PlatformWalletMonitor: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleError } = useApiError()
  const { currentEnv } = useNetworkEnvironment()
  const isTestnet = currentEnv === 'testnet'
  const mode: NetworkEnvironment = isTestnet ? 'testnet' : 'mainnet'
  const cardBg = useColorModeValue('white', 'gray.800')
  const sectionBg = useColorModeValue('gray.50', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const hoverBorderColor = useColorModeValue('blue.200', 'blue.500')
  const hoverShadow = useColorModeValue('sm', 'dark-lg')
  const belowThresholdBorderColor = useColorModeValue('red.400', 'red.300')
  const nearThresholdBorderColor = useColorModeValue('yellow.400', 'yellow.300')
  const healthyThresholdBorderColor = useColorModeValue('green.400', 'green.300')

  const [roleSnapshots, setRoleSnapshots] = useState<RoleWalletSnapshot[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [tronError, setTronError] = useState<string | null>(null)

  const loadTronRoleSnapshots = useCallback(async (): Promise<RoleWalletSnapshot[]> => {
    const wallets = await systemService.getSystemPlatformWallets(mode)
    const normalizedWallets = sortRoleWallets(
      wallets
        .map(normalizeRoleWallet)
        .filter(
          (item): item is RoleWalletEntry =>
            Boolean(item) && item?.compatibility === 'tron'
        )
    )

    if (normalizedWallets.length === 0) {
      return []
    }

    const fetchSnapshotWithRetry = async (
      wallet: RoleWalletEntry
    ): Promise<Awaited<ReturnType<typeof systemService.getPlatformWalletSnapshot>>> => {
      let lastError: unknown = null
      for (const retryDelay of SNAPSHOT_RETRY_DELAYS_MS) {
        if (retryDelay > 0) {
          await delay(retryDelay)
        }
        try {
          const snapshot = await systemService.getPlatformWalletSnapshot(
            { tronAddress: wallet.address },
            mode
          )
          if (snapshot) {
            return snapshot
          }
          lastError = new Error(t('monitoring.platformWallet.error.noData'))
        } catch (error) {
          lastError = error
        }
      }
      throw lastError ?? new Error(t('monitoring.platformWallet.error.noData'))
    }

    const snapshots = await mapWithConcurrency(
      normalizedWallets,
      SNAPSHOT_CONCURRENCY,
      async (wallet): Promise<RoleWalletSnapshot> => {
        try {
          const snapshot = await fetchSnapshotWithRetry(wallet)
          if (!snapshot) {
            return {
              wallet,
              chains: [],
              error: t('monitoring.platformWallet.error.noData'),
            }
          }

          const chains = sortByOrder(
            snapshot.chains.filter(chain => chain.compatibility === 'tron'),
            TRON_CHAIN_ORDER[mode]
          )

          return {
            wallet,
            chains,
            updatedAt: snapshot.updatedAt,
            error: chains.length === 0 ? t('monitoring.platformWallet.error.noData') : undefined,
          }
        } catch (error) {
          const message = resolveErrorMessage(error)
          return {
            wallet,
            chains: [],
            error: message || t('monitoring.platformWallet.error.noData'),
          }
        }
      }
    )

    return snapshots
  }, [mode, t])

  const loadRoleSnapshots = useCallback(async (): Promise<LoadRoleSnapshotsResult> => {
    const [balanceSnapshot, tronResult] = await Promise.all([
      systemService.getPlatformWalletBalances(mode),
      loadTronRoleSnapshots()
        .then(snapshots => ({ snapshots }))
        .catch(error => ({
          snapshots: [] as RoleWalletSnapshot[],
          error: resolveErrorMessage(error),
        })),
    ])

    if (!balanceSnapshot) {
      throw new Error(t('monitoring.platformWallet.error.noData'))
    }

    const balanceSnapshots = balanceSnapshot.wallets
      .map(wallet => normalizeBalanceMonitorWallet(
        wallet,
        mode,
        balanceSnapshot.updatedAt
      ))
      .filter((item): item is RoleWalletSnapshot => Boolean(item))

    return {
      snapshots: sortRoleSnapshots([
        ...balanceSnapshots,
        ...tronResult.snapshots,
      ]),
      tronError: 'error' in tronResult ? tronResult.error : undefined,
    }
  }, [loadTronRoleSnapshots, mode, t])

  const mutation = useMutation({
    mutationFn: loadRoleSnapshots,
    onSuccess: (data) => {
      setRoleSnapshots(data.snapshots)
      setTronError(data.tronError ?? null)
      if (data.snapshots.length === 0) {
        setError(new Error(t('monitoring.platformWallet.error.noData')))
      } else {
        setError(null)
      }
    },
    onError: (err: any) => {
      const failure = err instanceof Error ? err : new Error(String(err))
      setError(failure)
      toast.error({
        title: t('monitoring.platformWallet.error.title'),
        description: failure.message || t('monitoring.platformWallet.error.description'),
      })
      handleError(failure, 'Platform wallet snapshot')
    },
  })

  const { mutate, isPending } = mutation
  const resetState = useCallback(() => {
    setRoleSnapshots([])
    setError(null)
    setTronError(null)
  }, [])

  const handleRefresh = () => mutate()

  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address)
      toast.success(t('common.copySuccess'))
    } catch {
      toast.error(t('monitoring.platformWallet.error.description'))
    }
  }

  useEffect(() => {
    resetState()
    mutate()
  }, [mode, mutate, resetState])

  const evmRoleSnapshots = useMemo(
    () => roleSnapshots.filter(item => item.wallet.compatibility === 'evm'),
    [roleSnapshots]
  )

  const tronRoleSnapshots = useMemo(
    () => roleSnapshots.filter(item => item.wallet.compatibility === 'tron'),
    [roleSnapshots]
  )

  const solanaRoleSnapshots = useMemo(
    () => roleSnapshots.filter(item => item.wallet.compatibility === 'solana'),
    [roleSnapshots]
  )

  const renderRoleLabel = (role: SupportedRole): string =>
    t(`monitoring.platformWallet.roles.${role}`, { defaultValue: role })

  const renderChainStatus = (chain: PlatformWalletChainBalance): string => {
    if (chain.error) {
      return `${t('monitoring.platformWallet.labels.error')}: ${chain.error}`
    }
    if (chain.source) {
      return chain.source
    }
    return t('monitoring.platformWallet.labels.ok')
  }

  const renderApproxUsdt = (
    balance?: string | null,
    price?: number | null
  ) => {
    if (!balance) {
      return '~-- USDT'
    }
    if (!price || !Number.isFinite(price)) {
      return '~-- USDT'
    }
    const amount = Number(balance)
    if (!Number.isFinite(amount)) {
      return '~-- USDT'
    }
    return `~${formatApproxUsdt(amount * price)} USDT`
  }

  const renderTronUsageDeficit = (deficit?: TronUsageDeficit | null) => {
    if (!deficit) {
      return (
        <Text fontSize="xs" color="gray.500">
          {t('monitoring.platformWallet.usage.noData')}
        </Text>
      )
    }
    if (deficit.sufficient) {
      return (
        <Text fontSize="xs" color="green.500" fontWeight="semibold">
          {t('monitoring.platformWallet.usage.sufficient')}
        </Text>
      )
    }
    return (
      <Text fontSize="xs" color="orange.500" fontWeight="semibold">
        {t('monitoring.platformWallet.usage.deficit', {
          value: formatTrxValue(deficit.estimatedTrxDeficit),
        })}
      </Text>
    )
  }

  const resolveThresholdBorderColor = (
    chain: PlatformWalletChainBalance
  ): string => {
    switch (resolveMostSevereThresholdStatus(chain.thresholds)) {
      case 'below':
        return belowThresholdBorderColor
      case 'near':
        return nearThresholdBorderColor
      case 'healthy':
        return healthyThresholdBorderColor
      default:
        return borderColor
    }
  }

  const renderThreshold = (chain: PlatformWalletChainBalance) => {
    const thresholds = chain.thresholds ?? []
    if (thresholds.length === 0) {
      return null
    }

    return (
      <VStack align="stretch" mt={1} spacing={0.5}>
        {thresholds.map((threshold) => {
          const statusColorScheme = resolveThresholdColorScheme(threshold.status)
          return (
            <HStack key={threshold.type} justify="space-between" spacing={2}>
              <Text fontSize="xs" color="gray.600">
                {t(`monitoring.platformWallet.threshold.types.${threshold.type}`)}
              </Text>
              <HStack spacing={1.5}>
                <Text fontSize="xs" fontWeight="semibold">
                  {formatBalance(threshold.amount, chain.decimals)} {chain.symbol}
                </Text>
                {threshold.status && (
                  <Badge colorScheme={statusColorScheme} fontSize="10px">
                    {t(`monitoring.platformWallet.threshold.status.${threshold.status}`)}
                  </Badge>
                )}
              </HStack>
            </HStack>
          )
        })}
      </VStack>
    )
  }

  const renderNativeBalanceChains = (chains: PlatformWalletChainBalance[]) => (
    <VStack align="stretch" spacing={1.5}>
      {chains.map((chain) => {
        const thresholdBorderColor = resolveThresholdBorderColor(chain)
        const thresholdStatus = resolveMostSevereThresholdStatus(chain.thresholds)
        return (
          <Box
            key={`${chain.chainId}-${chain.chainCode}`}
            borderWidth={thresholdStatus ? '2px' : '1px'}
            borderColor={thresholdBorderColor}
            borderRadius="md"
            bg={sectionBg}
            p={1.5}
            aria-label={
              thresholdStatus
                ? t(`monitoring.platformWallet.threshold.status.${thresholdStatus}`)
                : undefined
            }
            transition="all 0.15s ease"
            _hover={{
              borderColor: thresholdStatus
                ? thresholdBorderColor
                : hoverBorderColor,
              boxShadow: hoverShadow,
              transform: 'translateY(-1px)',
            }}
          >
            <HStack justify="space-between" mb={0.5}>
              <ChainBadge
                chainId={chain.chainId}
                chainCode={chain.chainCode}
                chainName={chain.chainName}
                chainNumericId={chain.chainId}
                isTestnet={chain.network === 'testnet'}
              />
              <Badge colorScheme={chain.network === 'mainnet' ? 'green' : 'purple'} fontSize="10px">
                {chain.network === 'mainnet'
                  ? t('monitoring.platformWallet.network.mainnet')
                  : t('monitoring.platformWallet.network.testnet')}
              </Badge>
            </HStack>
            <HStack
              justify="space-between"
              align={{ base: 'start', md: 'center' }}
              flexWrap="wrap"
              rowGap={0.5}
              columnGap={2}
            >
              <Text fontSize="sm" fontWeight="semibold" lineHeight="1.2">
                {formatBalance(chain.balance, chain.decimals)} {chain.symbol}{' '}
                {renderApproxUsdt(chain.balance, chain.nativeTokenPrice)}
              </Text>
              <Text fontSize="xs" color={chain.error ? 'red.500' : 'gray.600'} noOfLines={1}>
                {renderChainStatus(chain)}
              </Text>
            </HStack>
            {renderThreshold(chain)}
          </Box>
        )
      })}
    </VStack>
  )

  const renderTronChains = (chains: PlatformWalletChainBalance[]) => (
    <VStack align="stretch" spacing={1.5}>
      {chains.map((chain) => {
        const resources = chain.resources
        const energyProgress = buildResourceProgress(
          resources?.energy.remaining,
          resources?.energy.limit
        )
        const bandwidthProgress = buildResourceProgress(
          resources?.bandwidth.remaining,
          resources?.bandwidth.limit
        )
        return (
          <Box
            key={`${chain.chainId}-${chain.chainCode}`}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            bg={sectionBg}
            p={1.5}
            transition="all 0.15s ease"
            _hover={{
              borderColor: hoverBorderColor,
              boxShadow: hoverShadow,
              transform: 'translateY(-1px)',
            }}
          >
            <HStack justify="space-between" mb={1.5}>
              <ChainBadge
                chainId={chain.chainId}
                chainCode={chain.chainCode}
                chainName={chain.chainName}
                chainNumericId={chain.chainId}
                isTestnet={chain.network === 'testnet'}
              />
              <Badge colorScheme={chain.network === 'mainnet' ? 'green' : 'purple'} fontSize="10px">
                {chain.network === 'mainnet'
                  ? t('monitoring.platformWallet.network.mainnet')
                  : t('monitoring.platformWallet.network.testnet')}
              </Badge>
            </HStack>

            <VStack align="stretch" spacing={0.5}>
              <Text fontSize="sm" fontWeight="semibold" lineHeight="1.2" noOfLines={1}>
                {formatBalance(chain.balance, chain.decimals)} {chain.symbol}{' '}
                {renderApproxUsdt(chain.balance, chain.nativeTokenPrice)}
              </Text>
              <Text fontSize="xs" color={chain.error ? 'red.500' : 'gray.600'} noOfLines={1}>
                {renderChainStatus(chain)}
              </Text>
            </VStack>

            <Box mt={1}>
              <VStack align="stretch" spacing={0.5}>
                <Text fontSize="xs" fontWeight="semibold">
                  {t('monitoring.platformWallet.usage.title')}
                </Text>
                <HStack justify="space-between" align="center">
                  <Text fontSize="xs" color="gray.600">
                    {t('monitoring.platformWallet.usage.deployment')}:
                  </Text>
                  {renderTronUsageDeficit(chain.usageDeficits?.deployment)}
                </HStack>
                <HStack justify="space-between" align="center">
                  <Text fontSize="xs" color="gray.600">
                    {t('monitoring.platformWallet.usage.interaction')}:
                  </Text>
                  {renderTronUsageDeficit(chain.usageDeficits?.interaction)}
                </HStack>
              </VStack>
            </Box>

            <Box mt={1}>
              <VStack align="stretch" spacing={1}>
                <Box>
                  <HStack justify="space-between" mb={0.5}>
                    <Text fontSize="xs" fontWeight="semibold">
                      {t('monitoring.platformWallet.labels.energy')}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {energyProgress.label}
                    </Text>
                  </HStack>
                  <Progress value={energyProgress.value} size="xs" borderRadius="full" mt={0.5} />
                </Box>
                <Box>
                  <HStack justify="space-between" mb={0.5}>
                    <Text fontSize="xs" fontWeight="semibold">
                      {t('monitoring.platformWallet.labels.bandwidth')}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {bandwidthProgress.label}
                    </Text>
                  </HStack>
                  <Progress value={bandwidthProgress.value} size="xs" borderRadius="full" mt={0.5} />
                </Box>
              </VStack>
            </Box>
          </Box>
        )
      })}
    </VStack>
  )

  const renderRoleCard = (
    item: RoleWalletSnapshot
  ) => {
    const updatedAtLabel = item.updatedAt
      ? formatDateTime(item.updatedAt, { fallback: '--' })
      : null

    return (
      <Box
        key={item.wallet.id}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        p={2}
        bg={sectionBg}
      >
        <HStack justify="space-between" mb={1}>
          <HStack spacing={1.5}>
            <Badge
              colorScheme={
                item.wallet.compatibility === 'evm'
                  ? 'blue'
                  : item.wallet.compatibility === 'solana'
                    ? 'orange'
                    : 'purple'
              }
              fontSize="10px"
            >
              {item.wallet.compatibility.toUpperCase()}
            </Badge>
            <Badge colorScheme="gray" fontSize="10px">
              {renderRoleLabel(item.wallet.role)}
            </Badge>
          </HStack>
          {updatedAtLabel && (
            <Text fontSize="10px" color="gray.500" noOfLines={1}>
              {updatedAtLabel}
            </Text>
          )}
        </HStack>

        <Box mb={1.5}>
          <HStack spacing={1.5}>
            <Text
              fontSize={item.wallet.compatibility === 'solana' ? '10px' : 'xs'}
              fontFamily="mono"
              noOfLines={1}
              flex="1"
            >
              {item.wallet.address}
            </Text>
            <Tooltip label={t('common.copy')}>
              <IconButton
                aria-label={t('common.copy')}
                icon={<FiCopy />}
                size="xs"
                variant="ghost"
                onClick={() => void handleCopyAddress(item.wallet.address)}
              />
            </Tooltip>
          </HStack>
        </Box>

        {item.configurationIssue && (
          <Alert status="warning" borderRadius="md" mb={1.5}>
            <AlertIcon />
            <Box>
              <AlertDescription>
                {t(
                  `monitoring.platformWallet.configuration.${item.configurationIssue}`
                )}
              </AlertDescription>
              {item.runtimeAddress && (
                <Text fontSize="xs" fontFamily="mono" mt={0.5} wordBreak="break-all">
                  {item.runtimeAddress}
                </Text>
              )}
            </Box>
          </Alert>
        )}

        {item.error ? (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{item.error}</AlertDescription>
          </Alert>
        ) : item.wallet.compatibility === 'evm'
          || item.wallet.compatibility === 'solana' ? (
          renderNativeBalanceChains(item.chains)
        ) : (
          renderTronChains(item.chains)
        )}
      </Box>
    )
  }

  return (
    <VStack spacing={2} align="stretch">
      {error && !isPending && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>{t('monitoring.platformWallet.error.title')}</AlertTitle>
            <AlertDescription>
              {error.message || t('monitoring.platformWallet.error.description')}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <Card bg={cardBg} borderRadius="lg">
        <CardHeader py={1.5}>
          <HStack justify="space-between" align="start">
            <Box>
              <HStack spacing={1.5}>
                <Text fontWeight="semibold" fontSize="xs">{t('monitoring.platformWallet.title')}</Text>
                <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                  {t('monitoring.platformWallet.labels.roleWalletCount', {
                    count: roleSnapshots.length,
                    defaultValue: `${roleSnapshots.length} role wallets`,
                  })}
                </Badge>
              </HStack>
              <Text fontSize="10px" color="gray.500">
                {t('monitoring.platformWallet.subtitle')}
              </Text>
            </Box>
            <HStack spacing={2}>
              <RefreshIconButton
                label={t('monitoring.platformWallet.refresh')}
                size="xs"
                isLoading={isPending}
                onClick={handleRefresh}
              />
            </HStack>
          </HStack>
        </CardHeader>
      </Card>

      <Card bg={cardBg} borderRadius="lg">
        <CardHeader py={1.5}>
          <HStack justify="space-between">
            <Text fontWeight="semibold" fontSize="xs">{t('monitoring.platformWallet.solana.title')}</Text>
            <Badge colorScheme="orange" variant="subtle">
              {t('monitoring.platformWallet.solana.count', { count: solanaRoleSnapshots.length })}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody pt={0} pb={2}>
          {solanaRoleSnapshots.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={1.5}>
              {solanaRoleSnapshots.map(renderRoleCard)}
            </SimpleGrid>
          ) : (
            <Text fontSize="xs" color="gray.500">
              {t('monitoring.platformWallet.error.noData')}
            </Text>
          )}
        </CardBody>
      </Card>

      <Card bg={cardBg} borderRadius="lg">
        <CardHeader py={1.5}>
          <HStack justify="space-between">
            <Text fontWeight="semibold" fontSize="xs">{t('monitoring.platformWallet.tron.title')}</Text>
            <Badge colorScheme="purple" variant="subtle">
              {t('monitoring.platformWallet.tron.count', { count: tronRoleSnapshots.length })}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody pt={0} pb={2}>
          {tronRoleSnapshots.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={1.5}>
              {tronRoleSnapshots.map(renderRoleCard)}
            </SimpleGrid>
          ) : tronError ? (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{tronError}</AlertDescription>
            </Alert>
          ) : (
            <Text fontSize="xs" color="gray.500">
              {t('monitoring.platformWallet.error.noData')}
            </Text>
          )}
        </CardBody>
      </Card>

      <Card bg={cardBg} borderRadius="lg">
        <CardHeader py={1.5}>
          <HStack justify="space-between">
            <Text fontWeight="semibold" fontSize="xs">{t('monitoring.platformWallet.evm.title')}</Text>
            <Badge colorScheme="blue" variant="subtle">
              {t('monitoring.platformWallet.evm.count', { count: evmRoleSnapshots.length })}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody pt={0} pb={2}>
          {evmRoleSnapshots.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1.5}>
              {evmRoleSnapshots.map(renderRoleCard)}
            </SimpleGrid>
          ) : (
            <Text fontSize="xs" color="gray.500">
              {t('monitoring.platformWallet.error.noData')}
            </Text>
          )}
        </CardBody>
      </Card>
    </VStack>
  )
}
